<template>
  <div class="profile-page">
    <PageHeader
      :title="t('authentication.profile.title')"
      :show-back="true"
      :back-label="t('authentication.profile.backButton')"
      @back="goBack"
    />

    <div class="profile-page__body">
      <section class="profile-page__card profile-page__card--avatar">
        <AvatarChip :initial="initial" :seed="avatarSeed" :size="96" />
        <p class="profile-page__hint">{{ t('authentication.profile.photoHint') }}</p>
      </section>

      <section class="profile-page__group">
        <h2 class="profile-page__group-name">{{ t('authentication.profile.identityTitle') }}</h2>
        <div class="profile-page__card">
          <label class="profile-page__field">
            <span class="profile-page__label">
              {{ t('authentication.profile.nameLabel') }}
              <span v-if="nameError" class="profile-page__error">{{ nameError }}</span>
            </span>
            <InputText
              v-model="form.name"
              autocomplete="name"
              :placeholder="t('authentication.profile.namePlaceholder')"
              :invalid="Boolean(nameError)"
              data-profile-name
              @update:model-value="nameError = ''"
            />
          </label>

          <!-- The primary email is chosen, never typed: the choices are the
               addresses the person's own logins have verified. With one such
               address there is nothing to choose, so it is shown and the way to
               add another is said. -->
          <div class="profile-page__field">
            <span id="profile-primary-email" class="profile-page__label">{{ t('authentication.profile.primaryEmailLabel') }}</span>
            <Select
              v-if="canChooseEmail"
              v-model="form.primaryEmail"
              :options="verifiedEmails"
              aria-labelledby="profile-primary-email"
              data-profile-primary-email
            />
            <span v-else class="profile-page__value" data-profile-primary-email>{{ form.primaryEmail }}</span>
            <span class="profile-page__hint">{{ emailHint }}</span>
          </div>

          <label class="profile-page__field">
            <span class="profile-page__label">{{ t('authentication.profile.phoneLabel') }}</span>
            <InputText
              :model-value="form.phone"
              type="tel"
              autocomplete="tel-national"
              inputmode="tel"
              placeholder="(555) 867-5309"
              data-profile-phone
              @update:model-value="formatPhone"
            />
          </label>
        </div>
      </section>

      <!-- What the last save came to. In the page, not a toast: an identity
           screen says its verdict where the person is looking. -->
      <p v-if="verdict" class="profile-page__verdict" :class="`profile-page__verdict--${verdict.kind}`" role="status" data-profile-verdict>
        {{ verdict.message }}
      </p>

      <div v-if="dirty" class="profile-page__actions">
        <Button
          :label="t('authentication.profile.discardButton')"
          severity="secondary"
          text
          :disabled="saving"
          data-profile-discard
          @click="reset"
        />
        <Button
          :label="saveLabel"
          :disabled="saving"
          data-profile-save
          @click="save"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { PageHeader } from '@hds/auto-auction-com-components-mobile';
import { AvatarChip } from '@hds/auto-auction-com-components-shared';
import { linkingStore, sessionStore } from '@hds/identity-client-sdk';

import { useBackOrHome } from './backOrHome.js';

defineOptions({ name: 'ProfilePage' });

// Profile: who the signed-in person is, and the one place they change it. One
// page for a phone and a desktop.
//
// The primary email is the reason this page moved here from the phone app. It
// used to be a field a person could read and not change. It is now a choice
// among the addresses their own logins vouch for — every admitted login
// verifies its address, so an address a login carries is verified by existing.

const { t } = useI18n();
const session = sessionStore();
const linking = linkingStore();
const goBack = useBackOrHome();

const user = computed(() => session.user);

// A stable key for the avatar's color, so the same person is the same color on
// every surface they appear on.
const avatarSeed = computed(() => String(user.value?._id ?? user.value?.id ?? ''));

const initial = computed(function () {
    if (user.value?.initial) {
        return user.value.initial;
    }
    const name = (user.value?.displayName ?? user.value?.name ?? '').trim();
    return name
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join('');
});

const form = reactive({ name: '', phone: '', primaryEmail: '' });
const saved = ref({ name: '', phone: '', primaryEmail: '' });

function readFrom(person) {
    form.name = person?.displayName ?? person?.name ?? '';
    form.phone = person?.phone ?? '';
    form.primaryEmail = person?.primaryEmail ?? '';
    saved.value = { name: form.name, phone: form.phone, primaryEmail: form.primaryEmail };
}

// Whether a save is on its way. Declared before the watch below reads it.
const saving = ref(false);

// Keep the form on the live session person, so a hydrate in the background or
// a different person signing in does not leave old values on screen — except
// while a save is on its way. The save patches the session at once, and
// reading that patch back would make the form look already saved: Save and
// Discard would vanish the moment they were pressed, and "Saving…" would never
// show. The save settles the form itself, when the server answers.
watch(user, function (person) {
    if (saving.value) {
        return;
    }
    readFrom(person);
}, { immediate: true });

// The addresses the person's logins vouch for, each once. The current primary
// is always among the choices, even before the logins have loaded.
const verifiedEmails = computed(function () {
    const fromLogins = linking.connections.map((connection) => connection.email).filter(Boolean);
    return [...new Set([saved.value.primaryEmail, ...fromLogins].filter(Boolean))];
});

const canChooseEmail = computed(() => verifiedEmails.value.length > 1);

const emailHint = computed(function () {
    if (canChooseEmail.value) {
        return t('authentication.profile.primaryEmailHint');
    }
    return t('authentication.profile.primaryEmailOnlyOne');
});

const dirty = computed(() => form.name !== saved.value.name
    || form.phone !== saved.value.phone
    || form.primaryEmail !== saved.value.primaryEmail);

const nameError = ref('');
const verdict = ref(null);

const saveLabel = computed(function () {
    if (saving.value) {
        return t('authentication.profile.savingButton');
    }
    return t('authentication.profile.saveChangesButton');
});

// A phone number is shown the North American way as it is typed, and stops
// at ten digits. What was typed is the input's own value, always a string.
function formatted(typed) {
    const digits = typed.replace(/\D/g, '').slice(0, 10);
    if (digits.length === 0) {
        return '';
    }
    if (digits.length < 4) {
        return `(${digits}`;
    }
    if (digits.length < 7) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    }
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

// The field is bound one way, and this is the way back. An eleventh digit
// formats to the same ten the field already held, and a value that does not
// change is not redrawn by Vue — the extra digit would stay on screen while
// the saved number stopped at ten. So what was typed is shown for one tick
// first, and the formatted number is then a change the field is redrawn with.
async function formatPhone(typed) {
    const next = formatted(typed);
    if (next === form.phone) {
        form.phone = typed;
        await nextTick();
    }
    form.phone = next;
}

function reset() {
    form.name = saved.value.name;
    form.phone = saved.value.phone;
    form.primaryEmail = saved.value.primaryEmail;
    nameError.value = '';
    verdict.value = null;
}

// Only what changed is sent.
function changes() {
    const patch = {};
    if (form.name !== saved.value.name) {
        patch.name = form.name.trim();
    }
    if (form.phone !== saved.value.phone) {
        patch.phone = form.phone.trim();
    }
    if (form.primaryEmail !== saved.value.primaryEmail) {
        patch.primaryEmail = form.primaryEmail;
    }
    return patch;
}

async function save() {
    if (form.name.trim().length === 0) {
        nameError.value = t('authentication.profile.requiredError');
        return;
    }
    saving.value = true;
    verdict.value = null;
    const before = { ...saved.value };
    try {
        const patch = changes();
        // Shown at once; the server's answer replaces it when it arrives.
        session.applyUserPatch(patch);
        await session.updateMe(patch);
        saved.value = { name: form.name, phone: form.phone, primaryEmail: form.primaryEmail };
        verdict.value = { kind: 'saved', message: t('authentication.profile.saved') };
    } catch {
        // Put back what was there, so the form says what is really saved.
        session.applyUserPatch(before);
        readFrom(session.user);
        verdict.value = { kind: 'failed', message: t('authentication.profile.saveFailed') };
    } finally {
        saving.value = false;
    }
}

// The person's logins are where the choice of addresses comes from.
onMounted(() => linking.refresh());
</script>

<style scoped>
.profile-page {
  max-width: 40rem;
  margin: 0 auto;
  color: var(--p-text-color);
  font-family: var(--p-font-body);
}

/* The header brings its own inset; this is the page's, beneath it. */
.profile-page__body {
  padding: 12px 20px 24px;
}

.profile-page__group {
  margin-bottom: 24px;
}

.profile-page__group-name {
  margin: 0 0 8px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--p-text-muted-color);
}

.profile-page__card {
  background: var(--p-content-background);
  border: 1px solid var(--p-content-border-color);
  border-radius: 16px;
}

.profile-page__card--avatar {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  padding: 24px 16px;
}

.profile-page__field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px 16px;
}

.profile-page__field + .profile-page__field {
  border-top: 1px solid var(--p-content-border-color);
}

.profile-page__label {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  font-weight: 600;
  color: var(--p-text-muted-color);
}

.profile-page__value {
  font-size: 15px;
}

.profile-page__hint {
  margin: 0;
  font-size: 12px;
  color: var(--p-text-muted-color);
}

.profile-page__error {
  color: var(--p-red-400);
}

.profile-page__verdict {
  margin: 0 0 16px;
  font-size: 14px;
}

.profile-page__verdict--saved {
  color: var(--p-primary-color);
}

.profile-page__verdict--failed {
  color: var(--p-red-400);
}

.profile-page__actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

/* A phone: the two buttons share the width. */
@media (max-width: 480px) {
  .profile-page__actions > * {
    flex: 1;
  }
}
</style>
